/**
 * Hooks de Sandbox CSV — cableados al backend real.
 *
 * Endpoints:
 *   GET  /api/sandbox?clientSlug=...   → lista runs del cliente
 *   GET  /api/sandbox/:runId           → run + results + summary
 *   POST /api/sandbox                  → crea run + encola job
 *
 * El fan-out cross-cliente lo hace el panel (similar al inbox).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/api";
import { useCurrentUser } from "./use-current-user";
import type { ClientSlug, SandboxResult, SandboxRun } from "@/types";

interface ApiSandboxRun {
  id: string;
  clientSlug: string;
  skillVersion: string;
  status: "queued" | "running" | "done" | "failed" | "cancelled";
  questionsCount: number;
  processedCount: number;
  triggeredBy: string | null;
  errorMessage: string | null;
  createdAt: string;
  finishedAt: string | null;
}

interface ApiSandboxResult {
  id: string;
  runId: string;
  questionId: string;
  questionText: string;
  decision: "respond" | "escalate" | "ignore" | "error" | null;
  responseBody: string | null;
  category: string | null;
  rationale: string | null;
  latencyMs: number | null;
  tokensIn: number | null;
  tokensOut: number | null;
  createdAt: string;
}

function apiRunToRun(r: ApiSandboxRun): SandboxRun {
  return {
    id: r.id,
    client: r.clientSlug as ClientSlug,
    fileName: null,
    totalQuestions: r.questionsCount,
    processed: r.processedCount,
    skillVersion: r.skillVersion,
    comparisonVersion: null,
    status:
      r.status === "done"
        ? "done"
        : r.status === "failed" || r.status === "cancelled"
          ? "failed"
          : r.status,
    createdAt: r.createdAt,
    finishedAt: r.finishedAt,
    createdBy: r.triggeredBy,
  };
}

function apiResultToResult(r: ApiSandboxResult): SandboxResult {
  const text =
    r.decision === "respond"
      ? r.responseBody ?? ""
      : r.decision === "escalate"
        ? `[escalado:${r.category ?? "?"}] ${r.rationale ?? ""}`
        : r.decision === "ignore"
          ? `[ignorado] ${r.rationale ?? ""}`
          : `[error] ${r.rationale ?? "sin detalle"}`;
  return {
    id: r.id,
    runId: r.runId,
    question: r.questionText,
    expectedAnswer: null,
    actualAnswer: text,
    comparisonAnswer: null,
    pass: r.decision !== "error" && r.decision !== null,
    diffSummary: null,
  };
}

export function useSandboxRuns() {
  const { data: user } = useCurrentUser();
  return useQuery<SandboxRun[]>({
    queryKey: ["sandboxRuns", user?.clientAccess],
    enabled: !!user,
    refetchInterval: 5_000,
    queryFn: async () => {
      if (!user) return [];
      const batches = await Promise.all(
        user.clientAccess.map((s) =>
          apiGet<{ items: ApiSandboxRun[] }>(`/sandbox?clientSlug=${s}&limit=50`)
            .then((r) => r.items)
            .catch(() => [] as ApiSandboxRun[]),
        ),
      );
      return batches
        .flat()
        .map(apiRunToRun)
        .sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
    },
  });
}

export function useSandboxRun(id: string | undefined) {
  return useQuery<SandboxRun | null>({
    queryKey: ["sandboxRun", id],
    enabled: !!id,
    refetchInterval: (q) => {
      const d = q.state.data;
      return d && (d.status === "queued" || d.status === "running") ? 2_000 : false;
    },
    queryFn: async () => {
      if (!id) return null;
      const { run } = await apiGet<{ run: ApiSandboxRun }>(`/sandbox/${id}`);
      return apiRunToRun(run);
    },
  });
}

export function useSandboxResults(runId: string | undefined) {
  return useQuery<SandboxResult[]>({
    queryKey: ["sandboxResults", runId],
    enabled: !!runId,
    refetchInterval: 5_000,
    queryFn: async () => {
      if (!runId) return [];
      const { results } = await apiGet<{ results: ApiSandboxResult[] }>(`/sandbox/${runId}`);
      return results.map(apiResultToResult);
    },
  });
}

interface CreateRunPayload {
  clientSlug: ClientSlug;
  platform: string;
  skillVersion: string;
  questions: { id: string; text: string }[];
}

interface CreateRunResponse {
  runId: string;
  status: string;
  totalQuestions: number;
}

export function useCreateSandboxRun() {
  const qc = useQueryClient();
  return useMutation<CreateRunResponse, Error, CreateRunPayload>({
    mutationFn: async (input) => apiPost<CreateRunResponse>("/sandbox", input),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["sandboxRuns"] });
      toast.success(`Run lanzado: ${data.totalQuestions} preguntas en cola`);
    },
    onError: (err) => toast.error(`No se pudo lanzar: ${err.message}`),
  });
}
